import { useState } from "react"
import cx from "clsx"
import type { FormModule, ModuleDefinitionField } from "shared"
import { AdminButton } from "@rawa/ui/AdminButton"
import { AdminDialog } from "./AdminDialog"
import { ModuleForm } from "@rawa/form-modules/ModuleForm"
import type { FieldValues } from "@rawa/form-modules/fieldValues"
import { Tag } from "@rawa/ui/Tag"
import typography from "@rawa/ui/styles/typography.module.css"
import { exampleValues } from "../utils/boundFields"
import styles from "./ModulePreviewDialog.module.css"

/**
 * Shows a module as a practitioner would see it on a session note.
 *
 * Open for as long as it is mounted. The form can be tried out, and what is
 * typed lives only that long, so each opening starts afresh. Bound fields start
 * at their bindings' examples: a changeable one can be changed, a display-only
 * one is read-only.
 */
export function ModulePreviewDialog({
  onClose,
  formModule,
  fields,
}: {
  onClose: () => void
  formModule: FormModule
  fields: ModuleDefinitionField[]
}) {
  const [values, setValues] = useState<FieldValues>({})

  return (
    <AdminDialog
      open
      onClose={onClose}
      labelledBy="preview-title"
      className={styles.dialog}
    >
      <h2 id="preview-title" className={cx(typography.h5, styles.title)}>
        {formModule.name} preview
      </h2>
      <div className={styles.form}>
        <Tag colour="aqua">{formModule.entityLinkLabel}</Tag>
        <h3 className={styles.name}>{formModule.name}</h3>
        <p className={styles.description}>{formModule.description}</p>
        <ModuleForm
          fields={fields}
          values={{ ...exampleValues(fields), ...values }}
          onChange={(id, value) =>
            setValues((held) => ({ ...held, [id]: value }))
          }
        />
      </div>
      <div className={styles.actions}>
        <AdminButton onClick={onClose}>Close preview</AdminButton>
      </div>
    </AdminDialog>
  )
}
